// Local session storage + getters for the signed-in user. The Firebase
// access_token is minted by the existing api/auth flow (Google /
// Microsoft OAuth) and persisted here for every middleware fetch.
//
// We deliberately key off the same `token` localStorage entry the
// legacy web/ app uses so a user already signed in on the v1 site
// stays signed in when they get flipped to v2.

const TOKEN_KEY = "token";
const EMAIL_KEY = "email";
const ROLE_KEY = "role";
const NAME_KEY = "name";
const DEFAULT_ORG_KEY = "default_organization";
const ORGS_KEY = "organizations";
const LOGO_LIGHT_KEY = "logo_light";
const LOGO_DARK_KEY = "logo_dark";

export interface SessionUser {
  email: string;
  name: string;
  role: string;
  defaultOrganization: string;
  organizations: string[];
  logoLight: string | null;
  logoDark: string | null;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getSession(): SessionUser | null {
  const token = getToken();
  if (!token) return null;
  try {
    return {
      email: localStorage.getItem(EMAIL_KEY) || "",
      name: localStorage.getItem(NAME_KEY) || "",
      role: localStorage.getItem(ROLE_KEY) || "Admin",
      defaultOrganization: localStorage.getItem(DEFAULT_ORG_KEY) || "",
      organizations: parseOrgs(localStorage.getItem(ORGS_KEY)),
      logoLight: localStorage.getItem(LOGO_LIGHT_KEY),
      logoDark: localStorage.getItem(LOGO_DARK_KEY),
    };
  } catch {
    return null;
  }
}

function parseOrgs(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((v) => (typeof v === "string" ? v : v?.organization ?? v?.name ?? ""))
        .filter(Boolean);
    }
  } catch {
    /* fall through */
  }
  return [];
}

export interface AuthCallbackPayload {
  access_token: string;
  email: string;
  role?: string;
  name?: string;
  default_organization?: string;
  all_organizations?: unknown[];
  logo_light?: string;
  logo_dark?: string;
  // Present when the user signed in mid-way through the MCP OAuth
  // consent flow — backend echoes back the original Claude.ai
  // authorize request so the consent page can POST it to
  // /oauth/authorize/complete with the freshly minted JWT.
  oauth_continuation?: {
    client_id: string;
    redirect_uri: string;
    code_challenge: string;
    code_challenge_method: string;
    state: string;
    scope?: string | null;
  };
  // Public-demo signup completion — handled by Login flow, not here.
  flow_kind?: "signup" | "login";
  next_url?: string;
}

export function persistSession(data: AuthCallbackPayload): void {
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.email) localStorage.setItem(EMAIL_KEY, data.email);
  if (data.role) localStorage.setItem(ROLE_KEY, data.role);
  if (data.name) localStorage.setItem(NAME_KEY, data.name);
  if (data.default_organization) {
    localStorage.setItem(DEFAULT_ORG_KEY, data.default_organization);
  }
  if (Array.isArray(data.all_organizations)) {
    localStorage.setItem(ORGS_KEY, JSON.stringify(data.all_organizations));
  }
  if (data.logo_light) localStorage.setItem(LOGO_LIGHT_KEY, data.logo_light);
  if (data.logo_dark) localStorage.setItem(LOGO_DARK_KEY, data.logo_dark);
}

export function setOrgLogos(light: string | null, dark: string | null): void {
  if (light) localStorage.setItem(LOGO_LIGHT_KEY, light);
  else localStorage.removeItem(LOGO_LIGHT_KEY);
  if (dark) localStorage.setItem(LOGO_DARK_KEY, dark);
  else localStorage.removeItem(LOGO_DARK_KEY);
}

export function clearSession(): void {
  for (const k of [TOKEN_KEY, EMAIL_KEY, ROLE_KEY, NAME_KEY, DEFAULT_ORG_KEY, ORGS_KEY, LOGO_LIGHT_KEY, LOGO_DARK_KEY]) {
    localStorage.removeItem(k);
  }
}
